/**
 * Pure, unit-tested filter logic behind the Lidovi (leads) list filter bar
 *. Kept out of the page component so the predicate +
 * distinct-value helpers are testable in isolation — mirrors offersFilter.ts.
 *
 * Client-side only: the page already loads the full RLS-scoped lead list ("svi
 * iz prodaje vide sve"), so filtering is done in memory while volume stays low.
 */
import type { Lead, LeadStage, LeadClassification } from "../api/leads";

/** Slice of Lead the predicate + distinct-value helpers actually need. */
export type FilterableLead = Pick<
  Lead,
  "name" | "contactPerson" | "email" | "phone" | "stage" | "ownerId" | "ownerName" | "country" | "classification"
>;

/** Sentinel country value standing in for leads with no country — grouped under "—" in the dropdown. */
export const EMPTY_COUNTRY = "__EMPTY__";

export type LeadFilterCriteria = {
  /** Free text over firm name / contact person / email / phone; "" = no filter. */
  search: string;
  /** "" = all stages; otherwise a LeadStage. */
  stage: string;
  /** ownerId; "" = all owners. */
  ownerId: string;
  /** "" = all countries; EMPTY_COUNTRY = the grouped "no country" bucket. */
  country: string;
  /** "" = all classifications; otherwise a LeadClassification. */
  classification: string;
};

export const EMPTY_LEAD_FILTER: LeadFilterCriteria = {
  search: "",
  stage: "",
  ownerId: "",
  country: "",
  classification: "",
};

/** Case-insensitive substring match over the lead's firm name, contact person, email and phone. */
export function matchesLeadSearch(lead: FilterableLead, term: string): boolean {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  return [lead.name, lead.contactPerson, lead.email, lead.phone]
    .some((v) => (v ?? "").toLowerCase().includes(q));
}

/** AND-combines every active criterion. */
export function filterLeads<T extends FilterableLead>(leads: T[], criteria: LeadFilterCriteria): T[] {
  return leads.filter((l) => {
    if (criteria.stage && l.stage !== (criteria.stage as LeadStage)) return false;
    if (criteria.ownerId && l.ownerId !== criteria.ownerId) return false;
    if (criteria.country) {
      const country = l.country || EMPTY_COUNTRY;
      if (country !== criteria.country) return false;
    }
    if (criteria.classification && l.classification !== (criteria.classification as LeadClassification)) return false;
    if (criteria.search && !matchesLeadSearch(l, criteria.search)) return false;
    return true;
  });
}

/** True when at least one criterion is active — drives the "clear filters" button visibility. */
export function hasActiveLeadFilter(criteria: LeadFilterCriteria): boolean {
  return (
    criteria.search !== "" ||
    criteria.stage !== "" ||
    criteria.ownerId !== "" ||
    criteria.country !== "" ||
    criteria.classification !== ""
  );
}

export type OwnerOption = { id: string; name: string };

/** Distinct owners (ownerId + display name) present in the loaded list, sorted by name. */
export function distinctOwners(leads: FilterableLead[]): OwnerOption[] {
  const byId = new Map<string, string>();
  for (const l of leads) {
    if (!byId.has(l.ownerId)) byId.set(l.ownerId, l.ownerName ?? "—");
  }
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Distinct country codes present in the loaded list, sorted; blanks collapse to EMPTY_COUNTRY, listed last. */
export function distinctCountries(leads: FilterableLead[]): string[] {
  const codes = new Set<string>();
  let hasEmpty = false;
  for (const l of leads) {
    if (l.country) codes.add(l.country);
    else hasEmpty = true;
  }
  const sorted = [...codes].sort((a, b) => a.localeCompare(b));
  return hasEmpty ? [...sorted, EMPTY_COUNTRY] : sorted;
}
